<template>
  <div class="flex flex-col">
    <!-- Drag and Drop Zone -->
    <div
      :class="[
        'border border-dashed rounded-lg text-center transition-all duration-200 border-grey-3',
        isDragOver
          ? 'border-blue-400 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400 bg-grey-5',
      ]"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @dragover="handleDragOver"
      @drop="handleDrop">
      <input
        ref="fileInput"
        :accept="acceptedTypes"
        :multiple="multiple"
        class="hidden"
        type="file"
        @change="handleFileSelect" />

      <button
        class="hover:text-blue-700 w-full h-full button-1 text-center items-center p-6"
        type="button"
        @click="openFileDialog">
        <svg
          class="size-4 text-gray-400 inline-block mr-1 stroke-0"
          stroke="currentColor"
          viewBox="0 0 13 12"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M6.54935 11.7556H6.50002C4.80935 11.7556 3.43335 10.3796 3.43335 8.68895V2.02228C3.43335 0.772949 4.45068 -0.244385 5.70002 -0.244385C6.94935 -0.244385 7.96668 0.772949 7.96668 2.02228V7.56228C7.96668 7.93828 7.82002 8.29028 7.55335 8.55562C7.28668 8.82095 6.93068 8.96228 6.55735 8.96228C5.79068 8.95695 5.16668 8.32895 5.16668 7.56228V2.68895C5.16668 2.46762 5.34535 2.28895 5.56668 2.28895C5.78802 2.28895 5.96668 2.46762 5.96668 2.68895V7.56228C5.96668 7.89028 6.23468 8.15962 6.56268 8.16228C6.71868 8.16228 6.87602 8.10095 6.98935 7.98762C7.10402 7.87428 7.16668 7.72362 7.16668 7.56228V2.02228C7.16668 1.21295 6.50935 0.555615 5.70002 0.555615C4.89068 0.555615 4.23335 1.21295 4.23335 2.02228V8.68895C4.23335 9.93828 5.25068 10.9556 6.50002 10.9556H6.54935C7.79202 10.9556 8.76668 9.95962 8.76668 8.68895V2.72362C8.76668 2.50228 8.94535 2.32362 9.16668 2.32362C9.38802 2.32362 9.56668 2.50228 9.56668 2.72362V8.68895C9.56668 10.4089 8.24135 11.7556 6.54935 11.7556Z" />
        </svg>
        {{ buttonText }}
      </button>
    </div>

    <!-- Help Text -->
    <p class="paragraph-3 mt-4 md:text-center text-gray-600">
      {{ helpText }}
    </p>

    <!-- Error Messages -->
    <div v-if="errors.length > 0" class="mt-2 gap-y-1">
      <p v-for="error in errors" :key="error" class="text-sm text-red-1">
        {{ error }}
      </p>
    </div>

    <!-- File List -->
    <div v-if="files.length > 0" class="mt-4 gap-y-2">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div class="flex items-center gap-3">
          <svg class="w-4 h-4 text-gray-500" viewBox="0 0 20 20">
            <path
              clip-rule="evenodd"
              d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              fill-rule="evenodd"></path>
          </svg>
          <div>
            <p class="text-sm font-medium text-gray-900">{{ file.name }}</p>
            <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
          </div>
        </div>
        <button
          class="text-red-500 hover:text-red-700 p-1"
          type="button"
          @click="removeFile(index)">
          <svg class="w-4 h-4" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M6 18L18 6M6 6l12 12"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useFileUpload, type FileUploadOptions } from '@/composables/useFileUpload'

interface Props {
  maxFiles?: number
  maxFileSize?: number
  allowedTypes?: string[]
  multiple?: boolean
  buttonText?: string
  helpText?: string
  modelValue?: File[]
}

const props = withDefaults(defineProps<Props>(), {
  maxFiles: 10,
  maxFileSize: 20 * 1024 * 1024, // 20MB
  allowedTypes: () => [],
  multiple: true,
  buttonText: 'Attach files',
  helpText: 'Attach up to 10 files. The max allowed size per file is 20 MB.',
  modelValue: () => [],
})

const emit = defineEmits<{
  'update:modelValue': [files: File[]]
  'filesChanged': [files: File[]]
  'error': [errors: string[]]
}>()

// Create file upload options from props
const uploadOptions: FileUploadOptions = {
  maxFiles: props.maxFiles,
  maxFileSize: props.maxFileSize,
  allowedTypes: props.allowedTypes,
  multiple: props.multiple,
}

// Use the file upload composable
const {
  files,
  fileInput,
  isDragOver,
  errors,
  handleFileSelect,
  handleDrop,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  openFileDialog,
  removeFile,
  formatFileSize,
} = useFileUpload(uploadOptions)

// Computed property for accepted file types
const acceptedTypes = computed(() => {
  return props.allowedTypes.length > 0 ? props.allowedTypes.join(',') : '*/*'
})

// Watch for changes in files and emit updates
watch(files, (newFiles) => {
  emit('update:modelValue', newFiles)
  emit('filesChanged', newFiles)
}, { deep: true })

// Watch for errors and emit them
watch(errors, (newErrors) => {
  if (newErrors.length > 0) {
    emit('error', newErrors)
  }
}, { deep: true })

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  // Only update if the external value is different from our internal state
  if (newValue.length !== files.value.length ||
      newValue.some((f, i) => f.name !== files.value[i].name || f.size !== files.value[i].size)) {
    files.value = [...newValue]
  }
}, { deep: true })

// Initialize with modelValue if provided
if (props.modelValue.length > 0) {
  files.value = [...props.modelValue]
}
</script>

<style scoped>
/* Any custom styles would go here */
</style>
